import moment from "moment";
import {
  Edit,
  LockClock,
  Visibility,
  Delete,
  ReportProblem,
  CheckCircle,
  AccessTime,
  Warning,
  Error as ErrorIcon,
  HelpOutline,
} from "@mui/icons-material";
import {
  Chip,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Paper,
  Box,
  Typography,
  Avatar,
} from "@mui/material";
import React, { useState } from "react";
import Loader from "../../components/Loader/Loader";
import NotFound from "../../components/NotFound/NotFound";
import JobEditModal from "./JobAction/JobEditModal";
import TaskDetailsModal from "../../components/PopupModal/TaskDetailsModal";
import { deleteScheduleJob } from "../../api/services/scheduling";

import { QK } from "../../base/qk";
import { useSnackbar } from "notistack";
import { useQueryClient } from "react-query";
import ConfirmModal from "../../components/ConfirmModal/ConfirmModal";
import { formatDateField, formatTime } from "../../base/FormatTime";
import { LocationAuditCell } from "../../components/VisitTimeline";

const PUNCTUALITY_ICONS = {
  check: CheckCircle,
  clock: AccessTime,
  warning: Warning,
  alert: ErrorIcon,
  help: HelpOutline,
};

const VISIT_TYPE_COLORS = {
  "Call Visit": { color: "#D97706", bg: "#FFFAEB", border: "#FEDF89" },
  "Direct Visit": { color: "#1570EF", bg: "#EFF8FF", border: "#B2DDFF" },
  "Template Visit": { color: "#7F56D9", bg: "#F9F5FF", border: "#D6BBFB" },
};

const PunctualityBadge = ({ punctuality }) => {
  if (!punctuality || punctuality.status === "UNKNOWN") {
    return (
      <Box
        sx={{
          display: "inline-flex",
          alignItems: "center",
          gap: 0.5,
          px: 1,
          py: 0.4,
          borderRadius: "6px",
          bgcolor: "#F2F4F7",
          border: "1px solid #EAECF0",
          color: "#667085",
          fontWeight: 700,
          fontSize: "0.7rem",
        }}
      >
        <HelpOutline sx={{ fontSize: 13 }} />
        No Data
      </Box>
    );
  }

  const IconComponent = PUNCTUALITY_ICONS[punctuality.icon] || CheckCircle;

  const displayText =
    punctuality.direction === "on_time"
      ? "On Time"
      : `${punctuality.minutes}m ${punctuality.direction}`;

  return (
    <Tooltip title={punctuality.label} arrow>
      <Box
        sx={{
          display: "inline-flex",
          alignItems: "center",
          gap: 0.5,
          px: 1,
          py: 0.4,
          borderRadius: "6px",
          bgcolor: punctuality.bg_color,
          border: `1px solid ${punctuality.border_color}`,
          color: punctuality.color,
          fontWeight: 700,
          fontSize: "0.7rem",
        }}
      >
        <IconComponent sx={{ fontSize: 13 }} />
        {displayText}
      </Box>
    </Tooltip>
  );
};

const VisitTmelineTable = ({ scheduleTimeline, isLoading }) => {
  const [showEditTimeModal, setShowEditTimeModal] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [showJobDetailsModal, setShowJobDetailsModal] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [status, setStatus] = useState("");
  const { enqueueSnackbar } = useSnackbar();
  const queryCLient = useQueryClient();
  const [jobId, setJobId] = useState(null);

  const calculateTimeDuration = (start, end) => {
    if (!start || !end) return null;
    const startTime = moment(start, "HH:mm:ss");
    const endTime = moment(end, "HH:mm:ss");
    if (endTime.isBefore(startTime)) endTime.add(1, "days");
    const duration = moment.duration(endTime.diff(startTime));
    const hours = Math.floor(duration.asHours());
    const minutes = duration.minutes();
    return `${hours > 0 ? `${hours}h ` : ""}${minutes}m`;
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "Pending":
        return "warning";
      case "Not Started":
        return "info";
      case "In Progress":
        return "primary";
      case "Partially Completed":
        return "secondary";
      case "Completed":
        return "success";
      case "Missed":
        return "error";
      case "Cancelled":
        return "default";
      case "Late":
        return "error";
      default:
        return "default";
    }
  };

  const handleEditTimeModalOpen = (id, status) => {
    setShowEditTimeModal(true);
    setJobId(id);
    setStatus(status);
  };
  const handleJobDetailsModalOpen = (id) => {
    setShowJobDetailsModal(true);
    setJobId(id);
  };
  const handleConfirmModalOpen = (id) => {
    setShowConfirmModal(true);
    setJobId(id);
  };

  const handleDeleteJob = async () => {
    setDeleteLoading(true);
    const response = await deleteScheduleJob(jobId);
    if (response.status === 204) {
      setShowConfirmModal(false);
      setJobId(null);
      enqueueSnackbar("Job Deleted Successfully", { variant: "success" });
      queryCLient.invalidateQueries(QK.scheduleTimeline);
    } else {
      enqueueSnackbar("Error Deleting Job", { variant: "error" });
    }
    setDeleteLoading(false);
  };

  if (isLoading) return <Loader />;
  if (scheduleTimeline?.results?.length === 0)
    return <NotFound message="No Scheduled Job Found" />;

  return (
    <Box sx={{ pt: 2 }}>
      <TableContainer
        component={Paper}
        elevation={0}
        sx={{
          width: "100%",
          overflowX: "auto",
          borderRadius: "12px",
          border: "1px solid #EAECF0",
          boxShadow: "0px 2px 8px rgba(0,0,0,0.02)",
        }}
      >
        <Table sx={{ minWidth: 1400 }} size="small" stickyHeader>
          <TableHead>
            <TableRow>
              {[
                "Visit Name",
                "Visit Type",
                "Service User",
                "Location Audit",
                "Day",
                "Date",
                "Area",
                "Planned",
                "Rescheduled",
                "Actual",
                "Punctuality",
                "Duration",
                "Carer",
                "Status",
                "Action",
              ].map((header) => (
                <TableCell
                  key={header}
                  sx={{
                    backgroundColor: "#F9FAFB",
                    color: "#475467",
                    fontWeight: 600,
                    fontSize: "0.75rem",
                    py: 2,
                    whiteSpace: "nowrap",
                  }}
                >
                  {header}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {scheduleTimeline?.results?.map((job) => {
              const hasSeriousBreach = job.employee_details?.some(
                (emp) => emp.punctuality?.is_late === true
              );
              const visitTypeStyle = VISIT_TYPE_COLORS[job.visit_type] || {
                color: "#475467",
                bg: "#F2F4F7",
                border: "#EAECF0",
              };

              return (
                <TableRow
                  key={job.id}
                  sx={{
                    transition: "all 0.3s ease",
                    position: "relative",
                    backgroundColor: hasSeriousBreach ? "#FFF1F0" : "inherit",
                    borderLeft: hasSeriousBreach
                      ? "6px solid #D92D20"
                      : "0px solid transparent",
                    "&:hover": {
                      backgroundColor: hasSeriousBreach ? "#FFE4E1" : "#F9FAFB",
                    },
                  }}
                >
                  <TableCell
                    sx={{
                      fontWeight: 600,
                      color: hasSeriousBreach ? "#B42318" : "#101828",
                    }}
                  >
                    <Box
                      sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
                    >
                      {hasSeriousBreach && (
                        <ReportProblem
                          sx={{ fontSize: 18, color: "#D92D20", mr: 0.5 }}
                        />
                      )}
                      {job.visit_name}
                      {job?.time_critical && (
                        <Tooltip title="Time Critical">
                          <LockClock sx={{ fontSize: 16, color: "#D32F2F" }} />
                        </Tooltip>
                      )}
                    </Box>
                  </TableCell>

                  <TableCell>
                    <Box
                      sx={{
                        display: "inline-flex",
                        alignItems: "center",
                        px: 1.2,
                        py: 0.4,
                        borderRadius: "6px",
                        bgcolor: visitTypeStyle.bg,
                        color: visitTypeStyle.color,
                        border: `1px solid ${visitTypeStyle.border}`,
                        fontWeight: 700,
                        fontSize: "0.68rem",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {job.visit_type || "—"}
                    </Box>
                  </TableCell>

                  <TableCell>
                    <Box
                      sx={{ display: "flex", alignItems: "center", gap: 1.5 }}
                    >
                      {job.profile_photo ? (
                        <Avatar
                          src={job.profile_photo}
                          alt={job.client_name}
                          sx={{
                            width: 32,
                            height: 32,
                            border: "1px solid",
                            borderColor: hasSeriousBreach
                              ? "#F04438"
                              : "primary.main",
                          }}
                        >
                          {job.client_name?.charAt(0).toUpperCase()}
                        </Avatar>
                      ) : (
                        <Avatar
                          sx={{
                            width: 32,
                            height: 32,
                            fontSize: "0.85rem",
                            fontWeight: 700,
                            bgcolor: hasSeriousBreach
                              ? "#FDA29B"
                              : "primary.light",
                            color: hasSeriousBreach
                              ? "#B42318"
                              : "primary.main",
                            border: "1px solid",
                            borderColor: hasSeriousBreach
                              ? "#F04438"
                              : "primary.main",
                          }}
                        >
                          {job.client_name?.charAt(0).toUpperCase()}
                        </Avatar>
                      )}
                      <Typography
                        variant="body2"
                        sx={{
                          fontWeight: 600,
                          color: hasSeriousBreach ? "#B42318" : "text.primary",
                        }}
                      >
                        {job.client_name}
                      </Typography>
                    </Box>
                  </TableCell>

                  <TableCell>
                    <LocationAuditCell job={job} />
                  </TableCell>

                  <TableCell>
                    <Typography
                      variant="caption"
                      sx={{
                        display: "inline-block",
                        fontWeight: 800,
                        fontSize: "0.7rem",
                        px: 1.2,
                        py: 0.5,
                        borderRadius: "4px",
                        color: hasSeriousBreach ? "#B42318" : "#475467",
                        bgcolor: hasSeriousBreach ? "#FEE4E2" : "#F2F4F7",
                        border: "1px solid",
                        borderColor: hasSeriousBreach ? "#FDA29B" : "#EAECF0",
                      }}
                    >
                      {job.weekly_day ? job.weekly_day.substring(0, 3) : "—"}
                    </Typography>
                  </TableCell>

                  <TableCell
                    sx={{
                      whiteSpace: "nowrap",
                      color: hasSeriousBreach ? "#B42318" : "inherit",
                    }}
                  >
                    {formatDateField(job.build_for)}
                  </TableCell>

                  <TableCell>
                    <Box
                      sx={{
                        display: "inline-flex",
                        alignItems: "center",
                        px: 1.5,
                        py: 0.5,
                        borderRadius: "20px",
                        bgcolor: hasSeriousBreach ? "#FEE4E2" : "primary.50",
                        color: hasSeriousBreach ? "#B42318" : "primary.700",
                        border: "1px solid",
                        borderColor: hasSeriousBreach
                          ? "#F97066"
                          : "primary.100",
                      }}
                    >
                      <Typography
                        variant="caption"
                        sx={{
                          fontWeight: 700,
                          textTransform: "uppercase",
                          fontSize: "0.7rem",
                        }}
                      >
                        {job.area || "N/A"}
                      </Typography>
                    </Box>
                  </TableCell>

                  <TableCell
                    sx={{ whiteSpace: "nowrap", fontSize: "0.75rem" }}
                  >
                    <Typography
                      variant="caption"
                      display="block"
                      sx={{ color: hasSeriousBreach ? "#B42318" : "inherit" }}
                    >
                      <b>S:</b> {formatTime(job.scheduled_start)}
                    </Typography>
                    <Typography
                      variant="caption"
                      display="block"
                      sx={{ color: hasSeriousBreach ? "#B42318" : "inherit" }}
                    >
                      <b>E:</b> {formatTime(job.scheduled_end)}
                    </Typography>
                    {job.scheduled_start && job.scheduled_end && (
                      <Typography
                        variant="caption"
                        sx={{
                          color: hasSeriousBreach ? "#D92D20" : "primary.main",
                          fontWeight: 700,
                          mt: 0.5,
                          display: "block",
                        }}
                      >
                        (
                        {calculateTimeDuration(
                          job.scheduled_start,
                          job.scheduled_end
                        )}
                        )
                      </Typography>
                    )}
                  </TableCell>

                  <TableCell
                    sx={{ whiteSpace: "nowrap", fontSize: "0.75rem" }}
                  >
                    {job.rescheduled_start ? (
                      <>
                        <Typography variant="caption" display="block">
                          <b>S:</b> {formatTime(job.rescheduled_start)}
                        </Typography>
                        <Typography variant="caption" display="block">
                          <b>E:</b> {formatTime(job.rescheduled_end)}
                        </Typography>
                      </>
                    ) : (
                      "--"
                    )}
                  </TableCell>

                  <TableCell>
                    {job.employee_details?.map((emp, i) => (
                      <Box key={i} sx={{ mb: 0.5, whiteSpace: "nowrap" }}>
                        <Typography
                          variant="caption"
                          sx={{
                            fontWeight: 700,
                            color: hasSeriousBreach ? "#B42318" : "inherit",
                          }}
                        >
                          {formatTime(emp.actual_start)} -{" "}
                          {formatTime(emp.actual_end)}
                        </Typography>
                        <Typography
                          variant="caption"
                          sx={{
                            color: hasSeriousBreach
                              ? "#B42318"
                              : "text.secondary",
                            display: "block",
                            fontSize: "0.65rem",
                          }}
                        >
                          ({emp.first_name})
                        </Typography>
                      </Box>
                    ))}
                  </TableCell>

                  <TableCell>
                    {job.employee_details?.map((emp, i) => (
                      <Box key={i} sx={{ mb: 1 }}>
                        <PunctualityBadge punctuality={emp.punctuality} />
                        <Typography
                          variant="caption"
                          sx={{
                            color: hasSeriousBreach ? "#B42318" : "#98A2B3",
                            ml: 0.8,
                            fontWeight: 600,
                            fontSize: "0.65rem",
                          }}
                        >
                          ({emp.first_name})
                        </Typography>
                      </Box>
                    ))}
                  </TableCell>

                  <TableCell>
                    {job.employee_details?.map((emp, i) => (
                      <Typography
                        key={i}
                        variant="caption"
                        display="block"
                        sx={{
                          fontWeight: 600,
                          color: hasSeriousBreach ? "#B42318" : "inherit",
                        }}
                      >
                        {emp.job_duration !== "00:00" ? emp.job_duration : "--"}
                        <span
                          style={{
                            color: hasSeriousBreach ? "#B42318" : "#98A2B3",
                            fontSize: "0.65rem",
                            marginLeft: "4px",
                          }}
                        >
                          ({emp.first_name})
                        </span>
                      </Typography>
                    ))}
                  </TableCell>

                  <TableCell>
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                      {job.employee_details?.map((emp, i) => (
                        <Chip
                          key={i}
                          label={emp.first_name}
                          size="small"
                          variant={hasSeriousBreach ? "filled" : "outlined"}
                          sx={{
                            fontSize: "0.65rem",
                            height: 20,
                            bgcolor: hasSeriousBreach ? "#F04438" : "inherit",
                            color: hasSeriousBreach ? "#FFF" : "inherit",
                          }}
                        />
                      ))}
                    </Box>
                  </TableCell>

                  <TableCell>
                    <Chip
                      label={job.status}
                      color={
                        hasSeriousBreach ? "error" : getStatusColor(job.status)
                      }
                      size="small"
                      sx={{
                        fontWeight: 800,
                        fontSize: "0.7rem",
                        textTransform: "uppercase",
                      }}
                    />
                  </TableCell>

                  <TableCell>
                    <Box sx={{ display: "flex", gap: 0.5 }}>
                      <IconButton
                        onClick={() => handleJobDetailsModalOpen(job.id)}
                        size="small"
                        sx={{ color: "#1570EF", bgcolor: "#EFF8FF" }}
                      >
                        <Visibility sx={{ fontSize: 18 }} />
                      </IconButton>
                      <IconButton
                        onClick={() =>
                          handleEditTimeModalOpen(job.id, job.status)
                        }
                        size="small"
                        sx={{ color: "#7F56D9", bgcolor: "#F9F5FF" }}
                      >
                        <Edit sx={{ fontSize: 18 }} />
                      </IconButton>
                      <IconButton
                        onClick={() => handleConfirmModalOpen(job.id)}
                        size="small"
                        sx={{
                          color: "#D92D20",
                          bgcolor: "#FEF3F2",
                          border: hasSeriousBreach
                            ? "1px solid #F97066"
                            : "none",
                        }}
                      >
                        <Delete sx={{ fontSize: 18 }} />
                      </IconButton>
                    </Box>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      <JobEditModal
        show={showEditTimeModal}
        handleClose={() => setShowEditTimeModal(false)}
        JobId={jobId}
        status={status}
      />
      <TaskDetailsModal
        show={showJobDetailsModal}
        handleClose={() => setShowJobDetailsModal(false)}
        id={jobId}
      />
      <ConfirmModal
        show={showConfirmModal}
        handleClose={() => setShowConfirmModal(false)}
        handleConfirm={handleDeleteJob}
        isLoading={deleteLoading}
        title="Delete Job"
        body="Are you sure you want to delete this job?"
        isDelete="true"
      />
    </Box>
  );
};

export default VisitTmelineTable;